/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  FormRootSchema,
  FormState,
  FormStateSchema,
} from '@gh/builder-shared';
import { BuilderPermission, EmailTemplateType } from '@gh/builder-shared-data';
import { isRouteForCreationMode, whenCreateOrEditMode } from '@gh/common-shared';
import { FormMode, getControl, getControlValue, UploadContext, UploadMultiSlot } from '@gh/core-ui';

import { omit } from 'lodash';

import { EmailTemplateDetails } from '../../resources/email-template.model';

@Injectable()
export class EmailTemplateDetailsFormState extends FormState<EmailTemplateDetails> {
  imagesSlot: UploadMultiSlot;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              schemaFactory: BuilderFormSchemaFactory,
              private uploadContext: UploadContext) {
    super(schemaFactory);
  }

  get submitValue(): EmailTemplateDetails {
    return <EmailTemplateDetails>omit(this.group.getRawValue(), 'images');
  }

  get type(): EmailTemplateType {
    return getControlValue(this.group, 'type');
  }

  build(emailTemplate: EmailTemplateDetails): void {
    this.ensureImagesSlot(emailTemplate.type);

    this.initSchema(emailTemplate);

    this.imagesSlot.setControl(getControl(this.group, 'images'));
  }

  protected buildSchema(emailTemplate: EmailTemplateDetails, { root }: FormStateSchema): FormRootSchema {
    const hasEditPermissions = emailTemplate.type &&
      this.userContext.hasPermission(BuilderPermission.Setup.Templates[emailTemplate.type].EDIT);

    let mode: FormMode;
    if (isRouteForCreationMode(this.route)) {
      mode = FormMode.CreateMode;
    } else {
      if (hasEditPermissions) {
        mode = FormMode.EditMode;
      } else {
        mode = FormMode.ViewMode;
      }
    }
    const isLayoutType = emailTemplate.type === EmailTemplateType.Layout;

    return root({
      enabled: whenCreateOrEditMode(),
      mode,
      optimisticLock: true,
      children: {
        id: void 0,
        name: { enabled: !isLayoutType },
        masterZoneId: void 0,
        officeId: void 0,
        type: void 0,
        header: void 0,
        footer: void 0,
        placeholders: void 0,
        description: void 0,
        templateTypeId: void 0,
        subject: { enabled: !isLayoutType, visible: !isLayoutType, required: true },
        images: { asyncValidators: [this.imagesSlot.formValidator()] },
        body: { required: true },
        bodyText: void 0,
      },
    });
  }

  private ensureImagesSlot(type?: EmailTemplateType): void {
    if (this.imagesSlot) {
      return;
    }

    this.imagesSlot = this.uploadContext.createUploadMultiSlot({
      entryPoint: {
        [EmailTemplateType.Layout]: 'LAYOUT_TEMPLATE',
        [EmailTemplateType.Internal]: 'INTERNAL_TEMPLATE',
        [EmailTemplateType.System]: 'SYSTEM_TEMPLATE',
        [EmailTemplateType.Job]: 'JOB_TEMPLATE',
      }[type || ''],
    });
  }
}
